import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Check, Copy, LogOut, Mail, RefreshCw, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

const reveal = (delay: string) => cn("animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both", delay);

export default function NoRoleAssigned() {
  const navigate = useNavigate();
  const { session, role, loading, refreshRole } = useAuth();
  const [checking, setChecking] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!session) {
      navigate("/auth");
      return;
    }
    if (role) {
      navigate("/");
    }
  }, [loading, session, role, navigate]);

  if (loading || !session || role) {
    return null;
  }

  const userId = session.user.id;
  const email = session.user.email ?? "your account";

  const handleCheckAgain = async () => {
    setChecking(true);
    try {
      const result = await refreshRole();
      if (result.kind === "role") {
        toast.success("Access granted — taking you in.");
        navigate("/");
        return;
      }
      if (result.kind === "no-role") {
        toast.info("No role has been assigned to your account yet.");
        return;
      }
      toast.error("Couldn't check your access right now. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      navigate("/auth");
    }
  };

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(userId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4">
      <AmbientBackdrop />

      <div
        className={cn(
          "relative w-full max-w-md rounded-2xl border border-border/70 bg-card/80 p-8 shadow-xl shadow-primary/5 backdrop-blur-sm",
          "animate-in fade-in zoom-in-95 duration-500",
        )}
      >
        <div className="flex flex-col items-center text-center">
          <Medallion />

          <p className={cn("mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-primary", reveal("delay-100"))}>
            Awaiting access
          </p>
          <h1 className={cn("mt-2 text-2xl font-semibold tracking-tight text-foreground", reveal("delay-150"))}>
            Your account needs a role
          </h1>
          <p className={cn("mt-3 text-sm leading-relaxed text-muted-foreground", reveal("delay-200"))}>
            You're signed in, but an administrator hasn't assigned you a role yet. Roles control which parts of the
            workspace you can see. Ask an administrator to grant you access, then check again.
          </p>
        </div>

        <div className={cn("mt-7 space-y-3 rounded-xl border border-border/70 bg-muted/40 p-4", reveal("delay-300"))}>
          <div className="flex items-center gap-3">
            <Mail className="size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="text-[0.7rem] uppercase tracking-wider text-muted-foreground">Signed in as</p>
              <p className="truncate text-sm font-medium text-foreground">{email}</p>
            </div>
          </div>
          <div className="border-t border-border/60" />
          <div className="flex items-center gap-3">
            <span className="grid size-4 shrink-0 place-items-center font-mono text-[0.6rem] text-muted-foreground">
              ID
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[0.7rem] uppercase tracking-wider text-muted-foreground">User ID</p>
              <p className="truncate font-mono text-xs text-foreground">{userId}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCopyId}
              className="h-8 shrink-0 text-muted-foreground"
            >
              {copied ? <Check className="text-emerald-500" /> : <Copy />}
              <span className="sr-only">Copy user ID</span>
            </Button>
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Share these details with your administrator so they can find your account.
          </p>
        </div>

        <div className={cn("mt-6 flex flex-col gap-2", reveal("delay-500"))}>
          <Button type="button" className="w-full" onClick={handleCheckAgain} disabled={checking}>
            <RefreshCw className={cn(checking && "animate-spin")} />
            {checking ? "Checking…" : "Check again"}
          </Button>
          <Button type="button" variant="outline" className="w-full" onClick={handleSignOut}>
            <LogOut />
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}

function Medallion() {
  return (
    <div className="relative grid size-20 place-items-center">
      <span className="absolute inset-0 rounded-full bg-primary/10" />
      <span className="absolute inset-2 rounded-full bg-primary/15" />
      <span className="absolute inset-0 rounded-full ring-1 ring-inset ring-primary/20 motion-safe:animate-ping motion-safe:[animation-duration:3s]" />
      <span className="relative grid size-12 place-items-center rounded-full bg-primary/15 text-primary">
        <ShieldAlert className="size-6" />
      </span>
    </div>
  );
}

function AmbientBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div
        className="absolute left-1/2 top-[-10%] h-[420px] w-[680px] -translate-x-1/2 rounded-full opacity-60 blur-3xl"
        style={{
          background: "radial-gradient(closest-side, hsl(var(--primary) / 0.18), transparent)",
        }}
      />
      <div
        className="absolute bottom-[-15%] right-[-5%] h-[360px] w-[360px] rounded-full opacity-50 blur-3xl"
        style={{
          background: "radial-gradient(closest-side, hsl(var(--primary) / 0.12), transparent)",
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: "radial-gradient(hsl(var(--foreground) / 0.06) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />
    </div>
  );
}
